import type { Sku } from '@commercelayer/sdk'
import type { ProductCardHorizontalProps } from '@design-system/components/UI/UIProductCardHorizontal.props'
import { getShippingMethods } from '@design-system/utils/shippingMethods'
import { getLineItemCardPricing } from '@design-system/utils/lineItemCardPricing'

/**
 * Product card and price recap of the isolated PDP express order, shaped like
 * the cart ones so the fast checkout sidesheet can reuse the cart components.
 */
export const useExpressOrderRecap = () => {
  const {
    order,
    lineItems,
    productLineItem: lineItem,
    couponLineItems,
    promoLineItems,
  } = useExpressOrder()
  const { currentWebsite } = useWebsite()
  const { formatPriceByLocale } = useFormatPrice()
  const { maybeFormatPrice, getShippingMethod, checkFlexPromotionsForOrder } =
    useCartUtilities()

  const currency = computed(() => order.value?.currency_code)

  const productCard = computed<ProductCardHorizontalProps | null>(() => {
    const item = lineItem.value
    if (!item) return null

    const sku = item.item as Sku | undefined
    const { itemTotalPrice, ...pricing } = getLineItemCardPricing(item)
    return {
      id: item.id,
      info: {
        name: item.name ?? '',
        ...pricing,
        color: item.metadata?.color,
        size: item.metadata?.size,
      },
      quantity: item.quantity,
      stockQty: sku?.inventory?.quantity,
      itemTotalPrice,
      available: sku?.inventory?.available ?? true,
      metadata: item.metadata?.website ?? currentWebsite.value,
      path: '#',
      image: item.image_url ?? '',
      skuCode: item.sku_code ?? '',
      canEditVariant: true,
    }
  })

  const subtotal = computed(() =>
    maybeFormatPrice(
      currency.value,
      lineItems.value.reduce(
        (accumulator, item) =>
          accumulator +
          (item.total_amount_float ?? 0) +
          (item.discount_float ?? 0),
        0
      )
    )
  )

  const discount = computed(() =>
    maybeFormatPrice(
      currency.value,
      couponLineItems.value.reduce(
        (accumulator, item) => accumulator + (item.total_amount_float ?? 0),
        0
      )
    )
  )

  const promoItems = computed(() =>
    promoLineItems.value.map(item => ({
      id: item.id,
      name: item.name ?? '',
      cost: formatPriceByLocale(
        item.currency_code ?? currency.value ?? '',
        item.total_amount_float ?? 0
      ),
    }))
  )

  const flexPromoShippingMethodIds = ref<string[]>([])
  watch(
    [
      () => order.value?.id,
      () => order.value?.coupon_code,
      () => lineItem.value?.quantity,
    ],
    async ([orderId]) => {
      flexPromoShippingMethodIds.value = orderId
        ? await checkFlexPromotionsForOrder(orderId)
        : []
    },
    { immediate: true }
  )

  const selectedShippingMethod = computed(() => {
    if (!order.value) return null
    const availableShippingMethods = getShippingMethods(order.value).map(
      getShippingMethod(order.value, flexPromoShippingMethodIds.value)
    )
    const selectedId = order.value.shipments?.[0]?.shipping_method?.id
    return (
      availableShippingMethods.find(method => method.id === selectedId) ??
      availableShippingMethods.find(method =>
        method.type.includes('Standard')
      ) ??
      availableShippingMethods[0] ??
      null
    )
  })

  const estimatedTaxAmountFloat = ref<number | null>(null)
  const estimatedTax = computed(() =>
    maybeFormatPrice(currency.value, estimatedTaxAmountFloat.value)
  )

  const total = computed(() => {
    if (!order.value) return null
    const shippingAmountFloat = order.value.shipments?.[0]?.shipping_method
      ? (order.value.shipping_amount_float ?? 0)
      : (selectedShippingMethod.value?.costFloat ?? 0)

    return maybeFormatPrice(
      currency.value,
      (order.value.subtotal_amount_float ?? 0) +
        (order.value.discount_amount_float ?? 0) +
        (estimatedTaxAmountFloat.value ?? 0) +
        shippingAmountFloat
    )
  })

  return {
    lineItem,
    productCard,
    subtotal,
    discount,
    promoItems,
    selectedShippingMethod,
    estimatedTaxAmountFloat,
    estimatedTax,
    total,
  }
}
